import { DecimalSchema } from "../../decimal/decimal.schema";
import Decimal from "decimal.js";
import { z } from "zod";

export const TradingCampaignRules = {
  FIXED_PER_VOLUME: "fixed-per-volume",
  PRIZE_BY_SLOT: "prize-by-slot",
  PROPORTIONAL: "proportional",
  PROPORTIONAL_V2: "proportional-v2",
  SPREAD_TRADES_OVER_TIME: "spread-trades-over-time",
  CUSTOM: "custom", // custom rule for testing (deprecated)
  PVP: "pvp",
  STAIRS: "stairs",
  GAUSSIAN: "gaussian",
  PNL: "pnl",
  POWER: "power",
} as const;
export type TradingCampaignRules = (typeof TradingCampaignRules)[keyof typeof TradingCampaignRules];

export const TradingCampaignRuleNameSchema = z.enum([
  TradingCampaignRules.FIXED_PER_VOLUME,
  TradingCampaignRules.PRIZE_BY_SLOT,
  TradingCampaignRules.PROPORTIONAL,
  TradingCampaignRules.PROPORTIONAL_V2,
  TradingCampaignRules.SPREAD_TRADES_OVER_TIME,
  TradingCampaignRules.CUSTOM,
  TradingCampaignRules.PVP,
  TradingCampaignRules.STAIRS,
  TradingCampaignRules.GAUSSIAN,
  TradingCampaignRules.PNL,
  TradingCampaignRules.POWER,
]);
export type TradingCampaignRuleList = z.infer<typeof TradingCampaignRuleNameSchema>;

export const WindowCooldownMode = {
  SLIDING_WINDOW: "slidding-window",
  FIXED_WINDOW: "fixed-window",
  NO_COOLDOWN: "no-cooldown",
} as const;
export type WindowCooldownMode = (typeof WindowCooldownMode)[keyof typeof WindowCooldownMode];
export const WindowCooldownModeSchema = z.nativeEnum(WindowCooldownMode);

export const FixedWindowCooldownConfigSchema = z.object({
  mode: z.literal(WindowCooldownMode.FIXED_WINDOW),
  windowDuration: z.number().optional(), // in hours (deprecated)
  windowDurationInMinutes: z.number().optional(), // the optional field is used to ensure backward compatibility with the old rules
  maxVolumeByWindowByUser: z.number(),
});
export const SlidingWindowCooldownConfigSchema = z.object({
  mode: z.literal(WindowCooldownMode.SLIDING_WINDOW),
  windowDuration: z.number().optional(), // in hours (deprecated)
  windowDurationInMinutes: z.number().optional(), // the optional field is used to ensure backward compatibility with the old rules
  maxVolumeByWindowByUser: z.number(),
});
export const NoCooldownConfigSchema = z.object({
  mode: z.literal(WindowCooldownMode.NO_COOLDOWN),
});

export const WindowCooldownConfigSchema = z
  .discriminatedUnion("mode", [
    FixedWindowCooldownConfigSchema,
    SlidingWindowCooldownConfigSchema,
    NoCooldownConfigSchema,
  ])
  .refine(
    (data) => data.mode === WindowCooldownMode.NO_COOLDOWN || data.windowDurationInMinutes || data.windowDuration,
    {
      message: "windowDuration or windowDurationInMinutes must be provided for sliding and fixed window modes",
      path: ["windowDurationInMinutes"],
    },
  )
  .refine(
    (data) =>
      data.mode === WindowCooldownMode.NO_COOLDOWN ||
      (data.windowDuration
        ? data.windowDuration >= 0.01
        : data.windowDurationInMinutes
          ? data.windowDurationInMinutes >= 1
          : false),
    {
      message:
        "windowDuration must be at least 0.01 or windowDurationInMinutes must be at least 1 when mode is not NO_COOLDOWN",
      path: ["windowDurationInMinutes"],
    },
  );

export type FixedWindowCooldownConfig = z.infer<typeof FixedWindowCooldownConfigSchema>;
export type SlidingWindowCooldownConfig = z.infer<typeof SlidingWindowCooldownConfigSchema>;
export type NoCooldownConfig = z.infer<typeof NoCooldownConfigSchema>;
export type WindowCooldownConfig = z.infer<typeof WindowCooldownConfigSchema>;

export const ProportionalRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.PROPORTIONAL),
  data: z.object({
    totalReward: DecimalSchema,
    kingOfTheHill: z.boolean().optional(),
  }),
});
export type ProportionalRule = z.infer<typeof ProportionalRuleSchema>;

export const ProportionalV2RuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.PROPORTIONAL_V2),
  data: z.strictObject({
    totalReward: DecimalSchema,
    kingOfTheHill: z.boolean().optional(),
    maxTotalVolumeByEpoch: z.number(),
    maxVolumeByUserByEpoch: z.number(),
    cooldown: WindowCooldownConfigSchema,
    /**
     * When enabled, scales the total reward pool proportionally based on actual volume vs max volume
     * (e.g., 50% total volume = 50% total rewards)
     */
    enableProportionalTotalReward: z.boolean().optional(),
    /**
     * Fairness threshold (0-1) for unreachable max volumes.
     * E.g., 0.2 uses 80% of max total volume as denominator for more generous rewards if the max total volume can't be reached.
     * However, if the total volume overflow the threshold, the total reward will be scaled proportionally to the total volume (by not taking into account the threshold).
     */
    eligibleVolumeThreshold: z.number().min(0).max(1).optional(),
  }),
});
export type ProportionalV2Rule = z.infer<typeof ProportionalV2RuleSchema>;

export const FixedPerVolumeRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.FIXED_PER_VOLUME),
  data: z.object({
    limit: z.number(),
    reward: DecimalSchema,
    requiredVolume: DecimalSchema,
    kingOfTheHill: z.boolean().optional(),
    cooldown: WindowCooldownConfigSchema.optional(),
  }),
});
export type FixedPerVolumeRule = z.infer<typeof FixedPerVolumeRuleSchema>;

export const PrizeBySlotRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.PRIZE_BY_SLOT),
  data: z.object({
    slots: z.array(
      z.object({
        quantity: z.number(),
        volume: DecimalSchema,
        reward: DecimalSchema,
      }),
    ),
    proportionalReward: DecimalSchema.optional().default(new Decimal(0)),
    shareUnGivenRewardProportionally: z.boolean().optional(),
    kingOfTheHill: z.boolean().optional(),
    maxTotalVolumeByEpoch: z.number().optional(),
    cooldown: WindowCooldownConfigSchema.optional(),
  }),
});
export type PrizeBySlotRule = z.infer<typeof PrizeBySlotRuleSchema>;

export const SpreadTradesOverTimeRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.SPREAD_TRADES_OVER_TIME),
  data: z.object({
    requiredTrades: z.number(),
    requiredVolumeByTrade: z.number(),
    intervalBetweenTrades: z.number(),
    limit: z.number(),
    reward: z.number(),
  }),
});
export type SpreadTradesOverTimeRule = z.infer<typeof SpreadTradesOverTimeRuleSchema>;

export const CustomRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.CUSTOM),
  data: z.object({
    raw: z.any(),
  }),
});
export type CustomRule = z.infer<typeof CustomRuleSchema>;

export const PvpRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.PVP),
  data: z.object({
    totalReward: DecimalSchema,
    phases: z.array(
      z.object({
        duration: z.number(),
        maxVolume: z.number(),
      }),
    ),
    limit: z.number().optional().default(0),
  }),
});
export type PvpRule = z.infer<typeof PvpRuleSchema>;

export const StairsRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.STAIRS),
  data: z.object({
    sortBy: z.enum(["volume", "time"]),
    limit: z.number(),
    totalReward: DecimalSchema,
    steps: z.array(
      z.object({
        reward: DecimalSchema,
        proportionalReward: z.boolean(),
        minVolume: z.number(),
      }),
    ),
    phases: z.array(
      z.object({
        duration: z.number(),
        maxVolume: z.number(),
      }),
    ),
  }),
});
export type StairsRule = z.infer<typeof StairsRuleSchema>;

export const GaussianRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.GAUSSIAN),
  data: z.object({
    sliceSize: z.number().min(1),
    minVolume: z.number().min(0), // minimum volume to start earning rewards
    maxVolumeByUserByEpoch: z.number().min(0),
    maxTotalVolumeByEpoch: z.number().min(0), // max overall volume for the epoch
    maxTotalWeightByEpoch: z.number().min(0), // max overall weight for the epoch
    totalReward: z.number().min(0),
    cooldown: WindowCooldownConfigSchema,
    idealVolume: z.number().min(0),
    deviation: z.number().min(0),
    /**
     * When enabled, scales the total reward pool proportionally based on actual volume vs max volume
     * (e.g., 50% total volume = 50% total rewards)
     */
    enableProportionalTotalReward: z.boolean().optional(),
    /**
     * Minimum percentage (0-1) of expected volume to use as denominator for reward calculation.
     * E.g., 0.8 means use at least 80% of expectedTradedVolume as the denominator.
     * If total volume is 5k and expected is 10k with threshold 0.8, denominator is 8k (not 5k).
     * If total volume exceeds the threshold (e.g., 9k), denominator becomes the actual total volume.
     */
    minimumExpectedVolumePercentage: z.number().min(0).max(1).optional(),
  }),
});
export type GaussianRule = z.infer<typeof GaussianRuleSchema>;
export const BalancedRuleSimulationDataSchema = z.object({
  weightByVolume: z.array(z.object({ volume: z.number(), weight: z.number() })),
  allUsersWeight: z.number(),
  weightsByUser: z.record(z.string(), z.number()),
});
export type BalancedRuleSimulationData = z.infer<typeof BalancedRuleSimulationDataSchema>;

export const PNL_GRAPH_DISPLAY_TYPE = {
  TOP_PNL: "top-pnl",
  TOP_LEADERBOARD: "top-leaderboard",
} as const;
export type PNL_GRAPH_DISPLAY_TYPE = (typeof PNL_GRAPH_DISPLAY_TYPE)[keyof typeof PNL_GRAPH_DISPLAY_TYPE];
export const PnlGraphDisplayTypeSchema = z.enum(PNL_GRAPH_DISPLAY_TYPE);

const POSITIVE_DECIMAL_STRING = /^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/;
const PositiveDecimalStringSchema = z
  .string()
  .refine((value) => POSITIVE_DECIMAL_STRING.test(value) && new Decimal(value).gt(0), {
    message: "must be a positive decimal string with up to 8 decimals",
  });

export const PNL_REWARD_SLOT_TICKETS_MAX_COUNT = 100;
export const PNL_REWARD_SLOT_TICKETS_MAX_EXPIRES_IN_DAYS = 365;

/** Free-entry tickets a prize slot grants each of its places, on top of (or instead of) its USDT reward. */
export const PnlRewardSlotTicketsSchema = z.object({
  count: z.number().int().positive().max(PNL_REWARD_SLOT_TICKETS_MAX_COUNT),
  faceValueUsdt: PositiveDecimalStringSchema,
  expiresInDays: z.number().int().positive().max(PNL_REWARD_SLOT_TICKETS_MAX_EXPIRES_IN_DAYS),
});
export type PnlRewardSlotTickets = z.infer<typeof PnlRewardSlotTicketsSchema>;

export const PnlRewardSlotSchema = z.object({
  quantity: z.number(),
  reward: DecimalSchema,
  tickets: PnlRewardSlotTicketsSchema.optional(),
});
export type PnlRewardSlot = z.infer<typeof PnlRewardSlotSchema>;

export const PnlRuleCmsSettingsSchema = z.object({
  rule: z.literal(TradingCampaignRules.PNL),
  data: z.object({
    pnlCoefficient: DecimalSchema,
    volumeCoefficient: DecimalSchema,
    /**
     * Minimum traded volume (USD) a participant must reach by the end of the
     * competition to be eligible for rewards. Applies to both spot (pnl) and
     * perps (perps-pnl) formats. A participant below this threshold is ranked
     * at the end of the leaderboard and earns no reward regardless of PnL.
     * Unset or `0` disables the gate (every participant is eligible). Negative
     * values are rejected so a malformed config can't silently disable the gate
     * via the `<= 0` path.
     */
    minVolumeUSD: DecimalSchema.refine((value) => value.gte(0), {
      message: "minVolumeUSD must be greater than or equal to 0",
    }).optional(),
    slots: z.array(PnlRewardSlotSchema),
    graphDisplayOptions: z
      .object({
        amount: z.number().int().positive(),
        type: PnlGraphDisplayTypeSchema,
      })
      .optional(),
    initialDeposit: z.number().min(0).default(0).optional(),
    isCompetition: z.boolean().default(false).optional(),
    /**
     * When true, uses perpetual futures PnL calculation (tracks long/short positions)
     * instead of spot trading PnL calculation (tracks buy/sell trades)
     */
    perps: z.boolean().optional(),
  }),
});
export type PnlRuleCmsSettings = z.infer<typeof PnlRuleCmsSettingsSchema>;

/**
 * A competition reward slot is invalid unless its quantity is a positive
 * integer and its reward a non-negative finite amount. Shared by the CMS
 * save-time refinement below and the API's prize-pool scaling
 * (`distributePrizePoolAcrossRewardSlots`) so the two layers cannot drift.
 * A zero reward is allowed: a slot earns nothing (harmless padding among
 * positive slots), and an all-zero distribution is how a free competition with
 * a 0 prize pool is expressed — notably Cede Arena free-tier 1v1 duels, whose
 * pool is `0 + 0` buy-ins. Non-finite is still rejected: "NaN"/"Infinity" both
 * parse through DecimalSchema and pass `isNegative()` but would scale every
 * slot to NaN. `distributePrizePoolAcrossRewardSlots` owns the zero-total
 * divisor case that this relaxation admits. A prize *config* additionally
 * rejects a slot paying neither USDT nor tickets ({@link isEmptyPnlRewardSlot}).
 */
export const isInvalidPnlRewardSlot = (slot: { quantity: number; reward: Decimal }): boolean =>
  !slot.reward.isFinite() || slot.reward.isNegative() || !Number.isInteger(slot.quantity) || slot.quantity <= 0;

/** A slot that pays nothing: no USDT and no tickets. Valid as stored data (a 0-pot payout), never as a prize config. */
export const isEmptyPnlRewardSlot = (slot: { reward: Decimal; tickets?: PnlRewardSlotTickets }): boolean =>
  slot.reward.isZero() && slot.tickets == null;

/** Rank i is paid the i-th expanded slot, so a lower place can never take more USDT than the one above it. */
export const areSlotsUsdtNonIncreasing = (slots: ReadonlyArray<{ reward: Decimal }>): boolean =>
  slots.every((slot, index) => {
    const above = slots[index - 1];
    return above == null || slot.reward.lte(above.reward);
  });

export const SLOTS_USDT_NON_INCREASING_MESSAGE = "Prize slots must be ordered by USDT reward, highest first";

/** USDT payouts are whole cents. */
export const PAYOUT_DECIMALS = 2;

export const SPIN_AND_GO_USDT_ONLY_PRIZES_MESSAGE = "Spin & Go prizes are USDT only";
export const EMPTY_PRIZE_SLOT_MESSAGE = "Every prize slot pays USDT, tickets, or both";
export const PAID_COMPETITION_NEEDS_USDT_PRIZE_MESSAGE = "A paid competition needs at least one USDT prize";

/** A free competition's `prizePool` as a Decimal, or null when it is missing or not a plain non-negative decimal. */
export const parseFreePrizePool = (prizePool: string | null | undefined): Decimal | null =>
  prizePool != null && /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(prizePool) ? new Decimal(prizePool) : null;

export const FREE_PRIZE_NO_SLOT_MESSAGE = "A free competition needs at least one prize slot";
export const FREE_PRIZE_SLOT_CENTS_MESSAGE = "Free competition prizes are paid in whole cents";
export const FREE_PRIZE_POOL_CENTS_MESSAGE = "A free competition's prize pool is in whole cents";
export const freePrizeTotalMismatchMessage = (slotsTotal: Decimal, prizePool: Decimal): string =>
  `Free competition prizes must add up to the prize pool (${slotsTotal.toString()} vs ${prizePool.toString()})`;

export type FreePrizeSlotsIssue =
  | { kind: "no_slot" }
  | { kind: "slot_not_whole_cents" }
  | { kind: "pool_not_whole_cents" }
  | { kind: "total_mismatch"; slotsTotal: Decimal; prizePool: Decimal };

export const freePrizeSlotsIssueMessage = (issue: FreePrizeSlotsIssue): string => {
  switch (issue.kind) {
    case "no_slot":
      return FREE_PRIZE_NO_SLOT_MESSAGE;
    case "slot_not_whole_cents":
      return FREE_PRIZE_SLOT_CENTS_MESSAGE;
    case "pool_not_whole_cents":
      return FREE_PRIZE_POOL_CENTS_MESSAGE;
    case "total_mismatch":
      return freePrizeTotalMismatchMessage(issue.slotsTotal, issue.prizePool);
  }
};

/**
 * A free competition pays fixed, Cede-funded prizes: its slots are literal USDT amounts in whole
 * cents adding up exactly to `prizePool`, itself in whole cents. Shared by the API entity, the
 * blueprint DTO and both CMS forms so they cannot drift.
 */
export function freePrizeSlotsIssues(
  slots: ReadonlyArray<{ quantity: number; reward: Decimal }>,
  prizePool: Decimal,
): FreePrizeSlotsIssue[] {
  const issues: FreePrizeSlotsIssue[] = [];
  if (prizePool.decimalPlaces() > PAYOUT_DECIMALS) issues.push({ kind: "pool_not_whole_cents" });
  if (slots.length === 0) return [{ kind: "no_slot" }, ...issues];
  if (slots.some((slot) => slot.reward.decimalPlaces() > PAYOUT_DECIMALS))
    issues.push({ kind: "slot_not_whole_cents" });
  const slotsTotal = slots.reduce((sum, slot) => sum.add(slot.reward.mul(slot.quantity)), new Decimal(0));
  if (!slotsTotal.eq(prizePool)) issues.push({ kind: "total_mismatch", slotsTotal, prizePool });
  return issues;
}

export const assertSlotsUsdtNonIncreasing = (
  slots: ReadonlyArray<{ reward: Decimal }>,
  ctx: z.RefinementCtx,
  path: (string | number)[],
): void => {
  if (!areSlotsUsdtNonIncreasing(slots))
    ctx.addIssue({ code: "custom", path, message: SLOTS_USDT_NON_INCREASING_MESSAGE });
};

/**
 * Competition-mode cross-field validation, applied wherever a PNL rule is
 * accepted from the CMS (blueprint save, campaign save). A competition with a
 * zero `initialDeposit` materializes paper accounts with $0 tradeable (the
 * Canberra bug), and a missing `graphDisplayOptions` leaves the Amount/Type
 * pre-settings undefined on the competition page — so when
 * `isCompetition === true` both become required.
 *
 * Kept as a standalone refinement (not baked into `PnlRuleCmsSettingsSchema`)
 * so legacy non-competition campaigns keep parsing unchanged.
 */
export const PnlCompetitionSettingsRefinement = (
  settings: PnlRuleCmsSettings,
  ctx: z.RefinementCtx<PnlRuleCmsSettings>,
): void => {
  if (!settings.data.isCompetition) return;
  if (settings.data.initialDeposit == null || settings.data.initialDeposit <= 0) {
    ctx.addIssue({
      code: "custom",
      message: "Competition mode requires a positive Initial Deposit (paper accounts are seeded with it)",
      path: ["data", "initialDeposit"],
    });
  }
  if (settings.data.graphDisplayOptions == null) {
    ctx.addIssue({
      code: "custom",
      message: "Competition mode requires Graph Display Options (Amount and Type)",
      path: ["data", "graphDisplayOptions"],
    });
  }
  // Competition slots are the reward distribution scaled to the real prize pool
  // at start (see the API's distributePrizePoolAcrossRewardSlots). Rejecting a
  // distribution that can't be scaled here — instead of at competition start,
  // after entry fees are consumed — is what keeps that failure out of production.
  if (settings.data.slots.some(isInvalidPnlRewardSlot)) {
    ctx.addIssue({
      code: "custom",
      message: "Competition reward slots require positive integer quantities and non-negative finite rewards",
      path: ["data", "slots"],
    });
  }
  assertSlotsUsdtNonIncreasing(settings.data.slots, ctx, ["data", "slots"]);
};

/** `PnlRuleCmsSettingsSchema` with the competition cross-field checks applied. */
export const PnlRuleCmsSettingsWithCompetitionChecksSchema = PnlRuleCmsSettingsSchema.superRefine(
  PnlCompetitionSettingsRefinement,
);

export const PowerRuleSchema = z.object({
  rule: z.literal(TradingCampaignRules.POWER),
  data: z.object({
    sliceSize: z.number().min(1),
    minVolume: z.number().min(0), // minimum volume to start earning rewards
    maxVolumeByUserByEpoch: z.number().min(0),
    maxTotalVolumeByEpoch: z.number().min(0), // max overall volume for the epoch
    maxTotalWeightByEpoch: z.number().min(0), // max overall weight for the epoch
    totalReward: z.number().min(0),
    cooldown: WindowCooldownConfigSchema,
    idealVolume: z.number().min(1),
    exponent: z.number().min(0),
    /**
     * When enabled, scales the total reward pool proportionally based on actual volume vs max volume
     * (e.g., 50% total volume = 50% total rewards)
     */
    enableProportionalTotalReward: z.boolean().optional(),
    /**
     * Minimum percentage (0-1) of expected volume to use as denominator for reward calculation.
     * E.g., 0.8 means use at least 80% of expectedTradedVolume as the denominator.
     * If total volume is 5k and expected is 10k with threshold 0.8, denominator is 8k (not 5k).
     * If total volume exceeds the threshold (e.g., 9k), denominator becomes the actual total volume.
     */
    minimumExpectedVolumePercentage: z.number().min(0).max(1).optional(),
  }),
});
export type PowerRule = z.infer<typeof PowerRuleSchema>;

export const TradingRuleSchema = z.discriminatedUnion("rule", [
  ProportionalRuleSchema,
  ProportionalV2RuleSchema,
  FixedPerVolumeRuleSchema,
  PrizeBySlotRuleSchema,
  SpreadTradesOverTimeRuleSchema,
  CustomRuleSchema,
  PvpRuleSchema,
  StairsRuleSchema,
  GaussianRuleSchema,
  PnlRuleCmsSettingsSchema,
  PowerRuleSchema,
]);
export type TradingCampaignRule = z.infer<typeof TradingRuleSchema>;

export const BalancedRuleSchema = z.discriminatedUnion("rule", [GaussianRuleSchema, PowerRuleSchema]);

export type BalancedRule = z.infer<typeof BalancedRuleSchema>;
